import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/backend/client';
import { trackSignUp, trackLogin, trackSocialAuth, trackAuthSuccess } from '@/lib/analytics';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signUp: (email: string, password: string, language?: string) => Promise<{ error: Error | null; needsConfirmation?: boolean }>;
  signIn: (email: string, password: string, rememberMe?: boolean) => Promise<{ error: Error | null }>;
  signInWithGoogle: () => Promise<{ error: Error | null }>;
  signOut: () => Promise<{ error: Error | null }>;
  resendConfirmationEmail: (email: string) => Promise<{ error: Error | null }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

function normalizeEmail(email: string) {
  return email.trim().toLowerCase();
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Re-sync session when page is restored from bfcache (browser back/forward).
    // Without this, the UI shows the pre-login "logged out" state after back navigation.
    const handlePageShow = (e: PageTransitionEvent) => {
      if (e.persisted) {
        supabase.auth.getSession().then(({ data: { session } }) => {
          setSession(session);
          setUser(session?.user ?? null);
        });
      }
    };
    window.addEventListener('pageshow', handlePageShow);

    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(prev => {
          // Avoid replacing with a new object when the token is the same
          if (prev?.access_token === session?.access_token) return prev;
          return session;
        });
        setUser(prev => {
          // Only replace when the actual user identity changes
          if (prev?.id === session?.user?.id && prev?.updated_at === session?.user?.updated_at) return prev;
          return session?.user ?? null;
        });
        setLoading(false);

        if (event === 'SIGNED_IN' && session?.user) {
          const u = session.user;
          const provider = (u.app_metadata?.provider as string) || 'email';
          const isSocial = provider === 'google' || provider === 'apple';

          // Detect new signup: account created within last 90 seconds
          const isNewUser = (Date.now() - new Date(u.created_at).getTime()) < 90_000;

          if (isSocial) {
            // Social OAuth redirects the page — onAuthStateChange is the ONLY
            // place we can reliably track these conversions.
            const socialProvider = provider as 'google' | 'apple';
            if (isNewUser) {
              trackSignUp(socialProvider);              // sign_up + Google Ads send_to
              trackSocialAuth('signup', socialProvider); // social_signup
            } else {
              trackLogin(socialProvider);               // login + Google Ads send_to
              trackSocialAuth('login', socialProvider);  // social_login
            }
            trackAuthSuccess(socialProvider);           // auth_success + Google Ads send_to
          }
        }
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    return () => {
      subscription.unsubscribe();
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, []);

  const signUp = async (email: string, password: string, language?: string) => {
    const normalizedEmail = normalizeEmail(email);

    // Use the canonical Supabase signup flow so production email delivery goes
    // through the project's auth email hook / queue instead of the legacy
    // client-invoked confirmation function.
    const { data, error } = await supabase.auth.signUp({
      email: normalizedEmail,
      password,
      options: {
        emailRedirectTo: `${window.location.origin}/`,
        data: {
          language: language ?? 'en',
        },
      },
    });

    if (error) {
      return { error: new Error(error.message), needsConfirmation: false };
    }

    return {
      error: null,
      needsConfirmation: !data.session,
    };
  };

  const resendConfirmationEmail = async (email: string) => {
    const normalizedEmail = normalizeEmail(email);
    // Resend via Supabase Auth — generates a fresh real token and delivers a functional email.
    const { error } = await supabase.auth.resend({
      type: 'signup',
      email: normalizedEmail,
      options: { emailRedirectTo: `${window.location.origin}/` },
    });
    return { error: error ? new Error(error.message) : null };
  };

  const signIn = async (email: string, password: string, rememberMe = true) => {
    const normalizedEmail = normalizeEmail(email);

    // Set persistence preference BEFORE calling signInWithPassword so the
    // adaptive storage adapter uses the right backend when saving the session.
    localStorage.setItem('qlivar_remember_me', rememberMe ? 'true' : 'false');

    const { data, error } = await supabase.auth.signInWithPassword({
      email: normalizedEmail,
      password,
    });

    if (!error) {
      trackLogin('email');
    }

    return { error: error ? new Error(error.message) : null };
  };

  const socialLoginInProgress = { current: false };

  const signInWithSocial = async (provider: 'google') => {
    if (socialLoginInProgress.current) return { error: null };
    socialLoginInProgress.current = true;

    // Social logins are always persistent — set flag before the OAuth redirect
    // so the session is stored in localStorage when the app resumes.
    localStorage.setItem('qlivar_remember_me', 'true');

    const redirectTo = `${window.location.origin}/dashboard`;

    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider,
        options: { redirectTo },
      });

      if (error) {
        return { error: new Error(error.message) };
      }

      return { error: null };
    } finally {
      // Reset after a delay so navigation can complete
      setTimeout(() => { socialLoginInProgress.current = false; }, 3000);
    }
  };

  const signInWithGoogle = async () => {
    return signInWithSocial('google');
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    return { error: error ? new Error(error.message) : null };
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      session, 
      loading, 
      signUp, 
      signIn, 
      signInWithGoogle, 
      signOut,
      resendConfirmationEmail
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
